import { useLanguageBanner } from '@hooks/useLanguageBanner'
import { getLanguageInfo } from '@constants/languages'
import styles from './LanguageBanner.module.css'

/**
 * Language Banner - Pure UI Component
 *
 * Displays a non-intrusive banner suggesting language change based on browser detection.
 *
 * Architecture:
 * - Pure presentational component (no logic, no state)
 * - Consumes useLanguageBanner hook for all decision logic
 * - Renders in the language suggested by browser (not current site language)
 *
 * SEO:
 * - Uses data-nosnippet to prevent indexing by search engines
 * - Positioned last in DOM, visually first via CSS
 *
 * Accessibility:
 * - No aria-live (static content, not dynamic message)
 * - Clear aria-labels on action buttons
 * - Flag images with aria-hidden (decorative)
 *
 * @component
 * @example
 * <LanguageBanner />
 */
export default function LanguageBanner () {
  const {
    showBanner,
    bannerLang,
    handleAccept,
    handleDismiss,
    translations
  } = useLanguageBanner()

  // Early return: Don't render if banner shouldn't be shown
  if (!showBanner || !bannerLang || !translations) {
    return null
  }

  // Get language metadata for display
  const suggestedLanguageInfo = getLanguageInfo(bannerLang)

  // Safety check: Ensure language info exists
  if (!suggestedLanguageInfo) {
    return null
  }

  // Replace {language} placeholder with actual language name
  const message = translations.message.replace('{language}', suggestedLanguageInfo.name)
  const acceptAriaLabel = translations.acceptAriaLabel.replace('{language}', suggestedLanguageInfo.name)

  return (
    <section
      className={styles.languageBanner}
      data-nosnippet
    >
      <div className={styles.content}>
        {/* Suggested language flag (decorative) */}
        <div className={styles.flag}>
          <img
            src={suggestedLanguageInfo.image}
            alt=''
            width='24'
            height='16'
            className={styles.flagImg}
            aria-hidden='true'
          />
        </div>

        {/* Message */}
        <div className={styles.message}>
          <p>{message}</p>
        </div>

        {/* Actions */}
        <div className={styles.actions}>
          <button
            type='button'
            onClick={handleAccept}
            className={styles.accept}
            aria-label={acceptAriaLabel}
          >
            {translations.accept}
          </button>

          <button
            type='button'
            onClick={handleDismiss}
            className={styles.dismiss}
            aria-label={translations.dismissAriaLabel}
          >
            {translations.dismiss}
          </button>
        </div>
      </div>
    </section>
  )
}
